import { Controller } from "@hotwired/stimulus"

// Provides inline quick edit popover for timeline week/rotation entry cards.
// Fetches a lightweight form via fetch, renders it in a viewport-aware popover,
// and submits updates via turbo stream.
export default class extends Controller {
  static values = {
    url: String,
  }

  connect() {
    this.popover = null
    this.boundHandleKeydown = this.handleKeydown.bind(this)
    this.boundHandleClickOutside = this.handleClickOutside.bind(this)
    this.boundCloseFromOther = this.closeFromOther.bind(this)

    document.addEventListener("timeline-quick-edit:opened", this.boundCloseFromOther)
  }

  disconnect() {
    this.removePopover()
    document.removeEventListener("timeline-quick-edit:opened", this.boundCloseFromOther)
  }

  openQuickEdit(event) {
    event.preventDefault()
    event.stopPropagation()

    // Notify other popovers to close
    document.dispatchEvent(
      new CustomEvent("timeline-quick-edit:opened", {
        detail: { element: this.element },
      }),
    )

    // If already open, close it (toggle behavior)
    if (this.popover) {
      this.close()
      return
    }

    const csrfToken = document.head
      .querySelector('meta[name="csrf-token"]')
      ?.getAttribute("content")

    fetch(this.urlValue, {
      credentials: "same-origin",
      headers: {
        "X-CSRF-Token": csrfToken,
        Accept: "text/html",
      },
    })
      .then((response) => response.text())
      .then((html) => {
        this.showPopover(html)
      })
      .catch((error) => {
        console.error("Error loading quick edit form:", error)
      })
  }

  // The page's weekend and Baustellen filter ride along, so the server recounts the row's
  // conflict badge over exactly the entries on screen.
  submitUrl(action) {
    const url = new URL(action, window.location.origin)
    new URLSearchParams(window.location.search).forEach((value, key) => {
      if (key === "hide_weekend" || key.startsWith("q[site_id_in]"))
        url.searchParams.append(key, value)
    })
    return url.toString()
  }

  submitForm(event) {
    event.preventDefault()

    const form = event.target
    const formData = new FormData(form)
    const csrfToken = document.head
      .querySelector('meta[name="csrf-token"]')
      ?.getAttribute("content")

    fetch(this.submitUrl(form.action), {
      method: "PATCH",
      credentials: "same-origin",
      headers: {
        "X-CSRF-Token": csrfToken,
        Accept: "text/vnd.turbo-stream.html",
      },
      body: formData,
    })
      .then(async (response) => {
        const contentType = response.headers.get("Content-Type") || ""
        const html = await response.text()

        if (response.ok && contentType.includes("text/vnd.turbo-stream.html")) {
          Turbo.renderStreamMessage(html)
          this.close()
        } else if (response.status === 422) {
          // Validation error — re-render form HTML in popover
          if (this.popover) {
            this.popover.innerHTML = html
            this.positionPopover()
          }
        }
      })
      .catch((error) => {
        console.error("Error submitting quick edit:", error)
      })
  }

  close() {
    this.removePopover()
  }

  // Private

  hideAllTooltips() {
    document.querySelectorAll(".plantafel-dynamic-tooltip").forEach((tooltip) => {
      tooltip.classList.add("hidden")
      tooltip.style.visibility = "hidden"
      tooltip.style.display = "none"
    })
    document.querySelectorAll('body > [id$="-tooltip"]').forEach((tooltip) => {
      tooltip.classList.add("hidden")
      tooltip.style.visibility = "hidden"
      tooltip.style.display = "none"
    })
  }

  showPopover(html) {
    this.removePopover()
    this.hideAllTooltips()

    const popover = document.createElement("div")
    popover.className =
      "fixed z-[9999] bg-white rounded-lg shadow-xl border border-gray-200 ring-1 ring-black/5"
    popover.innerHTML = html
    document.body.appendChild(popover)
    this.popover = popover

    // The popover lives on document.body, outside this controller's scope, so
    // Stimulus never binds the data-action handlers inside it. Wire them manually.
    this.bindPopoverActions(popover)

    this.positionPopover()

    // Add event listeners for closing
    document.addEventListener("keydown", this.boundHandleKeydown)
    // Delay click listener to avoid immediate close from the triggering click
    requestAnimationFrame(() => {
      document.addEventListener("click", this.boundHandleClickOutside)
    })
  }

  positionPopover() {
    if (!this.popover) return

    const trigger = this.element
    const rect = trigger.getBoundingClientRect()
    const popover = this.popover

    // Temporarily make visible to measure
    popover.style.visibility = "hidden"
    popover.style.display = "block"

    const popoverWidth = popover.offsetWidth
    const popoverHeight = popover.offsetHeight
    const padding = 8

    // Prefer positioning below the entry, aligned to the right edge
    let left = rect.right - popoverWidth
    let top = rect.bottom + padding

    // If not enough space below, position above
    if (top + popoverHeight > window.innerHeight - padding) {
      top = rect.top - popoverHeight - padding
    }

    // If still off-screen above, fall back to below
    if (top < padding) {
      top = rect.bottom + padding
    }

    // Ensure horizontal positioning stays in viewport
    if (left < padding) {
      left = padding
    }
    if (left + popoverWidth > window.innerWidth - padding) {
      left = window.innerWidth - popoverWidth - padding
    }

    popover.style.left = `${left}px`
    popover.style.top = `${top}px`

    // Self-correct for containing block offsets
    const placed = popover.getBoundingClientRect()
    const offsetX = placed.left - left
    const offsetY = placed.top - top

    if (Math.abs(offsetX) > 1 || Math.abs(offsetY) > 1) {
      popover.style.left = `${left - offsetX}px`
      popover.style.top = `${top - offsetY}px`
    }

    popover.style.visibility = "visible"
  }

  bindPopoverActions(popover) {
    const form = popover.querySelector("form")
    if (form) {
      form.addEventListener("submit", (event) => this.submitForm(event))
    }

    popover.querySelectorAll('[data-action*="timeline-quick-edit#close"]').forEach((element) => {
      element.addEventListener("click", (event) => {
        event.preventDefault()
        this.close()
      })
    })
  }

  removePopover() {
    if (this.popover) {
      this.popover.remove()
      this.popover = null
    }
    document.removeEventListener("keydown", this.boundHandleKeydown)
    document.removeEventListener("click", this.boundHandleClickOutside)
  }

  handleKeydown(event) {
    if (event.key === "Escape") {
      this.close()
    }
  }

  handleClickOutside(event) {
    if (
      this.popover &&
      !this.popover.contains(event.target) &&
      !this.element.contains(event.target)
    ) {
      this.close()
    }
  }

  closeFromOther(event) {
    if (event.detail.element !== this.element) {
      this.close()
    }
  }
}
